import { LockOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Col, Collapse, Form, Input, Row, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import {
  useGetUserInfo,
  useUserPasswordUpdate,
  useUserProfileUpdate,
} from "../../../hooks/api/user/hooks";
import {ShieldUser, User } from "lucide-react";
import { useMessageApi } from "../../../provider/AntdNotification";

const AdminProfile: React.FC = () => {
  const messageApi = useMessageApi();
  const { data } = useGetUserInfo();
  const admin_profile = data?.data;
  const [adminData, setAdminData] = useState(admin_profile ?? {});

  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    if (admin_profile) {
      setAdminData(admin_profile);
      profileForm.setFieldsValue(admin_profile); 
    }
  }, [admin_profile, profileForm]);

  const { mutate: updateProfile, isPending: isUpdatingProfile } =
    useUserProfileUpdate({
      onSuccess: (res) => {
        setAdminData(res.data);
        messageApi.success("Profile updated successfully");
      },
      onError: (err: any) => {},
    });
  const { mutate: updatePassword, isPending: isUpdatingPass } =
    useUserPasswordUpdate({
      onSuccess: (res) => {
        passwordForm.resetFields();
        setAdminData(res.data);
        messageApi.success("Password updated successfully");
      },
      onError: (err: any) => {
        messageApi.error(err?.response?.data?.message || err?.message || "Failed to update password");
      },
    });

  const items = [
    {
      key: "1",
      icon: <User className="w-4 h-4" />,
      label: "Profile Information",
      children: (
        <Form
          className="mt-8"
          form={profileForm}
          layout="vertical"
          initialValues={adminData}
          onFinish={(values) => {
            updateProfile(values);
          }}
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Please enter your name" }]}
              >
                <Input
                  placeholder="name"
                  className="py-2"
                  size="large"
                />
              </Form.Item>
            </Col>

            {/* <Col xs={24} md={12}>
              <Form.Item
                name="phone"
                label="Phone No."
                rules={[
                  { required: true, message: "Please enter your phone number" },
                  { len: 10, message: "Phone number must be 10 digits" },
                ]}
              >
                <Input
                  placeholder="Phone No."
                  type="number"
                  size="large"
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </Form.Item>
            </Col> */}

            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
              >
                <Input placeholder="Email" type="email" size="large"  />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="px-5 sm:py-5 sm:px-10 mt-3"
              icon={<SaveOutlined />}
              loading={isUpdatingProfile}
              style={{
                height: "40px",
                marginTop: "10px",
                borderRadius: "4px",
              }}
            >
              Save
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "2",
      label: "Change Password",
      icon: <ShieldUser className="w-4 h-4" />,
      children: (
        <Form
          className="mt-8"
          form={passwordForm}
          layout="vertical"
          onFinish={(values) => updatePassword(values)}
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[
              { required: true, message: "Please enter your current password" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "var(--color-primary)" }} />}
              placeholder="Current Password"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: "Please enter your new password" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "var(--color-primary)" }} />}
              placeholder="New Password"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="confirmNewPassword"
            label="Confirm New Password"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords do not match")
                  );
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "var(--color-primary)" }} />}
              placeholder="Confirm New Password"
              size="large"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={isUpdatingPass}
              style={{
                height: "40px",
                marginTop: "10px",
                borderRadius: "4px",
              }}
            >
              Update Password
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div style={{ margin: "0 auto" }} className="py-12">
    {/* {contextHolder} */}

      <div style={{ marginBottom: "20px" }}>
        <p style={{ color: "#666", margin: 0 }}>
          Manage your profile information and security settings
        </p>
      </div>

      <Tabs
        defaultActiveKey="1"
        items={items}
        type="card"
        style={{
          background: "#fff",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 51, 102, 0.08)",
        }}
      />
    </div>
  );
};

export default AdminProfile;
